'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Minus, Plus } from 'phosphor-icons';
import * as React from 'react';
import { c } from '../../../utils/tailwind-merge';

function CollapsiblePanel({
  className = '',
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={c('w-full', className)}
      {...props}
    />
  );
}

function CollapsiblePanelItem({
  className = '',
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      data-test="accordion-item"
      className={c(
        'mb-2 flex flex-col px-7 py-4',
        'rounded-lg border border-grey-300',
        className
      )}
      {...props}
    />
  );
}

function CollapsiblePanelTrigger({
  children,
  className = '',
  onValueChange,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  onValueChange?: (value: boolean) => void;
}) {
  return (
    <AccordionPrimitive.Header className={c('flex', className)}>
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={c(
          'group flex w-full flex-1 items-center justify-between gap-4 rounded-md',
          'body-small-medium text-grey-900',
          'text-left outline-none transition-all',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'disabled:pointer-events-none disabled:opacity-50'
        )}
        onClick={(e) => {
          const isOpen = e.currentTarget.getAttribute('data-state') === 'open';
          onValueChange?.(!isOpen);
        }}
        {...props}
      >
        {children}

        <span className="ml-8 flex items-center">
          <Plus
            size={24}
            className={c(
              'shrink-0 rounded p-1 transition',
              'group-hover:bg-grey-200 group-focus-visible:bg-grey-200',
              'group-data-[state=open]:hidden'
            )}
            color={'text-color-700'}
          />
          <Minus
            size={24}
            className={c(
              'hidden shrink-0 rounded p-1 transition',
              'group-hover:bg-grey-200 group-focus-visible:bg-grey-200',
              'group-data-[state=open]:inline-block'
            )}
            color={'text-color-700'}
          />
        </span>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function CollapsiblePanelContent({
  className = '',
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={c(
        'data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown',
        'duration-500'
      )}
      {...props}
    >
      <div className={c('overflow-hidden pt-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export {
  CollapsiblePanel,
  CollapsiblePanelContent,
  CollapsiblePanelItem,
  CollapsiblePanelTrigger,
};
